import { useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import LocationOn from '@mui/icons-material/LocationOn';
import { AppContext } from '../../context/AppContext';
import { PRODUCT_TYPES } from '../../utils/constants';
import visaLogo from '../../assets/logos/visa-logo.svg';
import mastercardLogo from '../../assets/logos/mastercard-logo.svg';
import amexLogo from '../../assets/logos/amex-logo.svg';
import discoverLogo from '../../assets/logos/discover-logo.svg';
import paypalLogo from '../../assets/logos/paypal.svg';
import { getCookieValue } from '../../helpers/cookieHelper';

const PAYMENT_METHOD = {
  CARD: 'card',
  PAYPAL: 'paypal',
};

const paymentTextBrown = '#4e342e';

const browserDotStyles = [
  { backgroundColor: '#ff5f56' },
  { backgroundColor: '#ffbd2e' },
  { backgroundColor: '#27c93f' },
];

const compactPreviewTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1,
    backgroundColor: '#fff',
    color: '#0F1214',
    '& fieldset': {
      borderColor: '#C7CCD1',
    },
    '&:hover fieldset': {
      borderColor: '#C7CCD1',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#C7CCD1',
      borderWidth: 1,
    },
  },
  '& .MuiInputBase-root': {
    height: { xs: 26, md: 46 },
    fontSize: { xs: '0.58rem', md: '0.95rem' },
    alignItems: 'center',
  },
  '& .MuiInputBase-input': {
    height: 'auto',
    boxSizing: 'border-box',
    lineHeight: 1.35,
    py: { xs: '6px', md: '12px' },
    color: '#0F1214',
  },
  '& .MuiOutlinedInput-input': {
    px: { xs: 1.25, md: 1.75 },
  },
  '& .MuiOutlinedInput-input.MuiInputBase-inputAdornedStart': {
    pl: 0,
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#6C737A',
    opacity: 1,
  },
  '& .MuiInputAdornment-root': {
    maxHeight: { xs: 26, md: 'none' },
  },
  '& .MuiInputLabel-root': {
    color: '#0F1214',
    fontSize: { xs: '0.58rem', md: '0.95rem' },
    lineHeight: 1.35,
    transform: { xs: 'translate(14px, 7px) scale(1)', md: 'translate(14px, 13px) scale(1)' },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#0F1214',
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    transform: { xs: 'translate(14px, -6px) scale(0.75)', md: 'translate(14px, -8px) scale(0.75)' },
  },
  '& .MuiSvgIcon-root': {
    fontSize: { xs: 12, md: 24 },
  },
};

const previewInputSlotProps = {
  htmlInput: {
    readOnly: true,
    tabIndex: -1,
    'aria-readonly': 'true',
  },
};

const previewControlInputProps = {
  tabIndex: -1,
  'aria-readonly': 'true',
};

const preventPreviewInteraction = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const CheckoutLiveView = () => {
  const { state, currencyOptions } = useContext(AppContext);
  const paymentMethod = '';
  const checkoutDomain = getCookieValue('checkout_domain') || null;

  const formatPrice = (price, currencyCode = 'USD') => {
    const amount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price || 0);
    const match = currencyOptions?.find((c) => c.code === currencyCode);
    const symbol = match?.symbol;
    if (symbol && String(symbol).trim() !== '') return `${symbol}${amount}`;
    return `${currencyCode} ${amount}`;
  };

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        p: { xs: 1, sm: 1, md: 3 },
        height: { xs: 'auto', md: '100%' },
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        width: { xs: '100%', md: '90%' },
        mx: 'auto',
        boxSizing: 'border-box',
      }}
    >
      {/* Browser Frame Simulation */}
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          width: '100%',
          height: 'auto',
        }}
      >
        <Box
          sx={{
            height: { xs: 24, md: 38 },
            borderBottom: '1px solid #eceff3',
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            px: { xs: 1, md: 2 },
            backgroundColor: '#fff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 0.75 } }}>
            {browserDotStyles.map((dotSx) => (
              <Box
                key={dotSx.backgroundColor}
                aria-hidden="true"
                sx={{
                  width: { xs: 4, md: 8 },
                  height: { xs: 4, md: 8 },
                  borderRadius: '50%',
                  ...dotSx,
                }}
              />
            ))}
          </Box>
          <Typography
            aria-label="Preview URL"
            sx={{
              width: { xs: 150, sm: 260, md: 340 },
              maxWidth: '42vw',
              height: { xs: 13, md: 20 },
              px: { xs: 1, md: 2 },
              borderRadius: 1,
              backgroundColor: '#f5f6f8',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: { xs: '0.38rem', md: '0.7rem' },
              lineHeight: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            https://{checkoutDomain?.toLocaleLowerCase() || 'domainname.com'}/checkout
          </Typography>
          <Box />
        </Box>

        {/* Checkout Page Content */}
        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Typography
            variant="h4"
            sx={{
              mb: { xs: 2.5, md: 4 },
              fontWeight: 600,
              textAlign: { xs: 'left', md: 'center' },
              width: '100%',
              fontSize: { xs: '0.8rem', md: '2.125rem' },
            }}
          >
            {state.businessInfo.companyName || 'Your Company Name'}
          </Typography>

          <Box sx={{ display: 'flex', gap: { xs: 3, md: 4 }, flexWrap: { xs: 'nowrap', md: 'wrap' } }}>
            {/* Left Column - Product Info */}
            <Box sx={{ flex: { xs: '1 1 0', md: '1 1 300px' }, minWidth: 0 }}>
              <Typography variant="h6" sx={{ mb: { xs: 0.5, md: 1 }, fontWeight: 600, fontSize: { xs: '0.65rem', md: '1.25rem' } }}>
                {state.product.name || 'Product Name'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 },
                  flexWrap: 'wrap',
                  mb: { xs: 1, md: 2 },
                  justifyContent: 'space-between',
                  rowGap: 1,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', fontSize: { xs: '0.95rem', md: '1.5rem' }, minWidth: 0, flex: '1 1 auto' }}>
                  {formatPrice(state.product.price, state.product.currency)}
                </Typography>
                <FormControl size="small" sx={{ flexShrink: 0, minWidth: { xs: 48, md: 72 }, '& .MuiInputBase-root': { height: { xs: 20, md: 40 }, fontSize: { xs: '0.5rem', md: '0.875rem' } } }}>
                  <InputLabel id="checkout-live-qty-label">QTY</InputLabel>
                  <Select
                    labelId="checkout-live-qty-label"
                    value={1}
                    label="QTY"
                    readOnly
                    inputProps={previewControlInputProps}
                    onChange={preventPreviewInteraction}
                    onMouseDown={preventPreviewInteraction}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <MenuItem key={num} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {state.product.imageUrl ? (
                <Box
                  component="img"
                  src={state.product.imageUrl}
                  alt={state.product.name || 'Product'}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 1,
                    mb: { xs: 1, md: 2 },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '4/3',
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 1, md: 2 },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Product Image
                  </Typography>
                </Box>
              )}

              <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.55rem', md: '1rem' }, lineHeight: { xs: 1.4, md: 1.5 } }}>
                {state.product.description || 'Product description goes here'}
              </Typography>

              {state.productType === PRODUCT_TYPES.DIGITAL && (
                <Box
                  sx={{
                    width: 'fit-content',
                    backgroundColor: 'lightgray',
                    padding: '5px 10px',
                    borderRadius: '20px',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: 'primary.main' }}
                  >
                    Digital Download
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Right Column - Billing & Payment */}
            <Box
              aria-label="Checkout page form preview"
              aria-readonly="true"
              sx={{
                flex: { xs: '1 1 0', md: '1 1 300px' },
                minWidth: 0,
                pointerEvents: 'none',
              }}
            >
              <Typography variant="h6" sx={{ mb: { xs: 1, md: 2 }, fontWeight: 600, fontSize: { xs: '0.65rem', md: '1.25rem' } }}>
                Billing Information
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 2 }, mb: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 } }}>
                  <TextField
                    label="First name*"
                    fullWidth
                    size="small"
                    value=""
                    slotProps={previewInputSlotProps}
                    sx={compactPreviewTextFieldSx}
                  />
                  <TextField
                    label="Last name*"
                    fullWidth
                    size="small"
                    value=""
                    slotProps={previewInputSlotProps}
                    sx={compactPreviewTextFieldSx}
                  />
                </Box>
                <TextField
                  label="Email*"
                  fullWidth
                  size="small"
                  value=""
                  slotProps={previewInputSlotProps}
                  sx={compactPreviewTextFieldSx}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked
                      inputProps={previewControlInputProps}
                      onChange={preventPreviewInteraction}
                      onClick={preventPreviewInteraction}
                    />
                  }
                  label={`Sign me up for ${state.businessInfo.companyName || 'Your Company Name'} emails.`}
                  sx={{ my: { xs: -0.5, md: 0 }, '& .MuiSvgIcon-root': { fontSize: { xs: 12, md: 24 } }, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.45rem', md: '0.75rem' } } }}
                />
                <TextField
                  label="Phone*"
                  fullWidth
                  size="small"
                  value=""
                  slotProps={previewInputSlotProps}
                  sx={compactPreviewTextFieldSx}
                />
                <TextField
                  label="Address*"
                  placeholder="742 Evergreen Terrace, Springfield, IL USA *"
                  fullWidth
                  size="small"
                  value=""
                  slotProps={previewInputSlotProps}
                  sx={compactPreviewTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Apt/Suite"
                  fullWidth
                  size="small"
                  value=""
                  slotProps={previewInputSlotProps}
                  sx={compactPreviewTextFieldSx}
                />
              </Box>

              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, color: paymentTextBrown, textAlign: 'left', display: { xs: 'none', md: 'block' } }}
              >
                Shipping Information
              </Typography>

              <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2, mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={false}
                      inputProps={previewControlInputProps}
                      onChange={preventPreviewInteraction}
                      onClick={preventPreviewInteraction}
                    />
                  }
                  label="Same as Billing Address"
                  sx={{ fontSize: '0.75rem' }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="First name*"
                    fullWidth
                    size="small"
                    value=""
                    slotProps={previewInputSlotProps}
                    sx={compactPreviewTextFieldSx}
                  />
                  <TextField
                    label="Last name*"
                    fullWidth
                    size="small"
                    value=""
                    slotProps={previewInputSlotProps}
                    sx={compactPreviewTextFieldSx}
                  />
                </Box>

                <TextField
                  label="Address*"
                  placeholder="742 Evergreen Terrace, Springfield, IL USA *"
                  fullWidth
                  size="small"
                  value=""
                  slotProps={previewInputSlotProps}
                  sx={compactPreviewTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Apt/Suite"
                  fullWidth
                  size="small"
                  value=""
                  slotProps={previewInputSlotProps}
                  sx={compactPreviewTextFieldSx}
                />
              </Box>

              <Typography
                variant="h6"
                sx={{ mb: { xs: 1, md: 2 }, fontWeight: 600, color: paymentTextBrown, textAlign: 'left', fontSize: { xs: '0.65rem', md: '1.25rem' } }}
              >
                Payment Information
              </Typography>

              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <RadioGroup
                  value=""
                  onChange={preventPreviewInteraction}
                  sx={{ width: '100%', gap: { xs: 1, md: 2 }, mb: { xs: 1, md: 2 } }}
                >
                  <Box
                    sx={{
                      border: '1px solid #cfd8dc',
                      borderRadius: 2,
                      px: { xs: 0.75, md: 2 },
                      py: { xs: 0.5, md: 1.5 },
                      transition: 'border-width 0.15s ease, border-color 0.15s ease',
                    }}
                  >
                    <FormControlLabel
                      value={PAYMENT_METHOD.CARD}
                      control={
                        <Radio
                          inputProps={previewControlInputProps}
                          onClick={preventPreviewInteraction}
                          sx={{
                            color: '#9e9e9e',
                            py: 0,
                            '&.Mui-checked': { color: '#000' },
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.75, md: 2 },
                            flexWrap: 'wrap',
                            width: '100%',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography sx={{ color: paymentTextBrown, fontWeight: 500 }}>
                            Credit or Debit Card
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: { xs: 0.5, md: 1 },
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              component="img"
                              src={visaLogo}
                              alt=""
                              sx={{ height: { xs: 8, md: 22 }, width: 'auto', display: 'block' }}
                            />
                            <Box
                              component="img"
                              src={mastercardLogo}
                              alt=""
                              sx={{ height: { xs: 8, md: 22 }, width: 'auto', display: 'block' }}
                            />
                            <Box
                              component="img"
                              src={amexLogo}
                              alt=""
                              sx={{ height: { xs: 8, md: 22 }, width: 'auto', display: 'block' }}
                            />
                            <Box
                              component="img"
                              src={discoverLogo}
                              alt=""
                              sx={{ height: { xs: 8, md: 22 }, width: 'auto', display: 'block' }}
                            />
                          </Box>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        mx: 0,
                        width: '100%',
                        alignItems: 'center',
                        '& .MuiSvgIcon-root': { fontSize: { xs: 10, md: 24 } },
                        '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                        '& .MuiFormControlLabel-label .MuiTypography-root': { fontSize: { xs: '0.55rem', md: '1rem' } },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid #cfd8dc',
                      borderRadius: 2,
                      px: { xs: 0.75, md: 2 },
                      py: { xs: 0.5, md: 1.5 },
                      transition: 'border-width 0.15s ease, border-color 0.15s ease',
                    }}
                  >
                    <FormControlLabel
                      value={PAYMENT_METHOD.PAYPAL}
                      control={
                        <Radio
                          inputProps={previewControlInputProps}
                          onClick={preventPreviewInteraction}
                          sx={{
                            color: '#9e9e9e',
                            py: 0,
                            '&.Mui-checked': { color: '#000' },
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.75, md: 2 },
                            flexWrap: 'wrap',
                            width: '100%',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography sx={{ color: paymentTextBrown, fontWeight: 500 }}>
                            PayPal
                          </Typography>
                          <Box
                            component="img"
                            src={paypalLogo}
                            alt="PayPal"
                            sx={{ height: { xs: 9, md: 24 }, width: 'auto', display: 'block' }}
                          />
                        </Box>
                      }
                      sx={{
                        m: 0,
                        mx: 0,
                        width: '100%',
                        alignItems: 'center',
                        '& .MuiSvgIcon-root': { fontSize: { xs: 10, md: 24 } },
                        '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                        '& .MuiFormControlLabel-label .MuiTypography-root': { fontSize: { xs: '0.55rem', md: '1rem' } },
                      }}
                    />
                  </Box>
                </RadioGroup>

                {paymentMethod === PAYMENT_METHOD.CARD && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Card number"
                      fullWidth
                      size="small"
                      value=""
                      slotProps={previewInputSlotProps}
                      sx={compactPreviewTextFieldSx}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="MM/YY"
                        fullWidth
                        size="small"
                        value=""
                        slotProps={previewInputSlotProps}
                        sx={compactPreviewTextFieldSx}
                      />
                      <TextField
                        label="CVC"
                        fullWidth
                        size="small"
                        value=""
                        slotProps={previewInputSlotProps}
                        sx={compactPreviewTextFieldSx}
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              <Button
                variant="contained"
                fullWidth
                disabled
                sx={{
                  backgroundColor: '#9e9e9e',
                  color: '#fff',
                  py: { xs: 0.5, md: 1.5 },
                  fontSize: { xs: '0.55rem', md: '0.875rem' },
                }}
              >
                Place Order
              </Button>
            </Box>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'center', mt: 4 }}
          >
            © {new Date().getFullYear()} {state.businessInfo.companyName || 'Companyname'}. All Rights Reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CheckoutLiveView;
